import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useSchoolId } from '../lib/auth/schoolContext';
import { ROLES, useUserRole, hasRole } from '../lib/auth/roles';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import SectionFProvisionPanel from './SectionFProvisionPanel';
import toast from 'react-hot-toast';
import {
  AREA_OF_NEED_LABELS,
  PROVISION_CATEGORY_LABELS,
} from '../lib/apdrSchemas';
import {
  getPresetsGroupedByCategory,
  getPresetByKey,
} from '../lib/provisionCatalogue';
import {
  SCHOOL_WEEKS_PER_YEAR,
  computeStudentProvisionCosts,
  resolveProvisionCostDisplay,
} from '../features/ehcna/lib/provisionCost';
import KbResourceLinks from './KbResourceLinks';

const AREA_TABS = [
  { id: 'all', label: 'All' },
  { id: 'cognitionLearning', label: 'C&L' },
  { id: 'communicationInteraction', label: 'C&I' },
  { id: 'semh', label: 'SEMH' },
  { id: 'sensoryPhysical', label: 'S&P' },
];

const EMPTY_FORM = {
  areaOfNeed: 'cognitionLearning',
  presetKey: '',
  name: '',
  category: 'staff_support',
  frequency: 'Daily',
  duration: '',
  ratio: '1:1',
  deliveryContext: 'in_lessons',
  subjectAreas: '',
  provisionType: 'staff',
  staffId: '',
  staffRoleLabel: '',
  resourceCostAnnual: '',
  startDate: '',
  endDate: '',
  status: 'active',
  notes: '',
};

export default function StudentProvisionPanel({ studentId, student, onProvisionChange }) {
  const { schoolId } = useSchoolId();
  const { role } = useUserRole();
  const canEdit = hasRole(role, [ROLES.SENCO]);
  const [provisions, setProvisions] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [areaFilter, setAreaFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    if (!studentId || !schoolId) return;
    setLoading(true);
    try {
      const [provSnap, staffSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'studentProvisions'),
          where('schoolId', '==', schoolId),
          where('studentId', '==', studentId)
        )),
        getDocs(query(
          collection(db, 'staff'),
          where('schoolId', '==', schoolId),
          where('active', '==', true)
        )),
      ]);
      setProvisions(provSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.areaOfNeed || '').localeCompare(b.areaOfNeed || '')));
      setStaff(staffSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      toast.error('Could not load provision records');
    } finally {
      setLoading(false);
    }
  }, [studentId, schoolId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    if (areaFilter === 'all') return provisions;
    return provisions.filter((p) => p.areaOfNeed === areaFilter);
  }, [provisions, areaFilter]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((p) => {
      const key = p.areaOfNeed || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return groups;
  }, [filtered]);

  const costPreview = useMemo(() => {
    const staffMember = form.staffId ? staff.find((s) => s.id === form.staffId) : null;
    const draft = {
      ...form,
      resourceCostAnnual: Number(form.resourceCostAnnual) || 0,
    };
    return computeStudentProvisionCosts(draft, staffMember);
  }, [form, staff]);

  const handlePresetChange = (presetKey) => {
    const preset = getPresetByKey(presetKey);
    if (!preset) {
      setForm((f) => ({ ...f, presetKey: '', name: '' }));
      return;
    }
    setForm((f) => ({
      ...f,
      presetKey,
      name: preset.name,
      category: preset.category,
      frequency: preset.defaultFrequency || f.frequency,
      duration: preset.defaultDuration || '',
      ratio: preset.defaultRatio || f.ratio,
      provisionType: preset.defaultProvisionType || 'other',
      resourceCostAnnual: preset.defaultResourceCostAnnual ?? '',
    }));
  };

  const openAdd = (areaOfNeed = 'cognitionLearning') => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, areaOfNeed });
    setShowForm(true);
  };

  const openEdit = (provision) => {
    setEditingId(provision.id);
    setForm({
      areaOfNeed: provision.areaOfNeed || 'cognitionLearning',
      presetKey: provision.presetKey || '',
      name: provision.name || '',
      category: provision.category || 'staff_support',
      frequency: provision.frequency || 'Weekly',
      duration: provision.duration || '',
      ratio: provision.ratio || '',
      deliveryContext: provision.deliveryContext || 'in_lessons',
      subjectAreas: (provision.subjectAreas || []).join(', '),
      provisionType: provision.provisionType || 'staff',
      staffId: provision.staffId || '',
      staffRoleLabel: provision.staffRoleLabel || '',
      resourceCostAnnual: provision.resourceCostAnnual ?? '',
      startDate: provision.startDate || '',
      endDate: provision.endDate || '',
      status: provision.status || 'active',
      notes: provision.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!canEdit || !studentId || !schoolId) return;
    if (!form.name.trim()) {
      toast.error('Provision name is required');
      return;
    }
    setSaving(true);
    try {
      const staffMember = form.staffId ? staff.find((s) => s.id === form.staffId) : null;
      const costs = computeStudentProvisionCosts(
        { ...form, resourceCostAnnual: Number(form.resourceCostAnnual) || 0 },
        staffMember
      );
      const payload = {
        studentId,
        schoolId,
        areaOfNeed: form.areaOfNeed,
        category: form.category,
        presetKey: form.presetKey || null,
        name: form.name.trim(),
        frequency: form.frequency,
        duration: form.duration || null,
        ratio: form.ratio || null,
        deliveryContext: form.deliveryContext || null,
        subjectAreas: form.subjectAreas
          ? form.subjectAreas.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        provisionType: form.provisionType,
        staffId: form.staffId || null,
        staffRoleLabel: form.staffRoleLabel || null,
        resourceCostAnnual: Number(form.resourceCostAnnual) || 0,
        weeklyCost: costs.weeklyCost,
        totalAnnual: costs.totalAnnual,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        status: form.status,
        notes: form.notes || null,
        updatedAt: serverTimestamp(),
      };

      if (editingId) {
        await updateDoc(doc(db, 'studentProvisions', editingId), payload);
        toast.success('Provision updated');
      } else {
        await addDoc(collection(db, 'studentProvisions'), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        toast.success('Provision added');
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadData();
      onProvisionChange?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not save provision');
    } finally {
      setSaving(false);
    }
  };

  const handleEnd = async (provision) => {
    if (!canEdit) return;
    try {
      await updateDoc(doc(db, 'studentProvisions', provision.id), {
        status: 'ended',
        endDate: new Date().toISOString().slice(0, 10),
        updatedAt: serverTimestamp(),
      });
      toast.success('Provision marked as ended');
      await loadData();
      onProvisionChange?.();
    } catch (err) {
      toast.error('Could not update provision');
    }
  };

  const handleDelete = async (provisionId) => {
    if (!canEdit || !window.confirm('Delete this provision record?')) return;
    try {
      await deleteDoc(doc(db, 'studentProvisions', provisionId));
      toast.success('Provision deleted');
      await loadData();
      onProvisionChange?.();
    } catch (err) {
      toast.error('Could not delete provision');
    }
  };

  const presetGroups = getPresetsGroupedByCategory(form.areaOfNeed);

  if (loading) {
    return <p className="text-sm text-gray-500">Loading provision...</p>;
  }

  return (
    <div className="space-y-4">
      <SectionFProvisionPanel
        studentId={studentId}
        student={student}
        onAccepted={() => {
          loadData();
          onProvisionChange?.();
        }}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Student provision</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            In-class support, adjustments, equipment, and access, separate from pull-out interventions.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => openAdd(areaFilter === 'all' ? 'cognitionLearning' : areaFilter)}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Add provision
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {AREA_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setAreaFilter(tab.id)}
            className={`rounded-full px-3 py-1 text-xs font-medium border ${
              areaFilter === tab.id
                ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {Object.keys(grouped).length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-gray-500">
            No provision recorded yet. Add TA support, timeout cards, sensory equipment, and other adjustments here.
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([area, items]) => (
          <Card key={area}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{AREA_OF_NEED_LABELS[area] || area}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {items.map((p) => {
                const staffMember = p.staffId ? staff.find((s) => s.id === p.staffId) : null;
                const staffName = staffMember
                  ? [staffMember.firstName, staffMember.lastName].filter(Boolean).join(' ')
                  : p.staffRoleLabel;
                return (
                  <div
                    key={p.id}
                    className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{p.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {PROVISION_CATEGORY_LABELS[p.category] || p.category}
                        {p.frequency ? ` • ${p.frequency}` : ''}
                        {p.duration ? ` • ${p.duration}` : ''}
                        {p.ratio && p.ratio !== 'n/a' ? ` • ${p.ratio}` : ''}
                        {staffName ? ` • ${staffName}` : ''}
                      </p>
                      {p.notes && <p className="text-xs text-gray-600 mt-1">{p.notes}</p>}
                      <span className={`inline-flex mt-2 px-2 py-0.5 text-xs rounded-full ${
                        p.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {p.status}
                      </span>
                    </div>
                    {canEdit && (
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openEdit(p)} className="text-xs text-indigo-600 hover:underline">Edit</button>
                        {p.status === 'active' && (
                          <button type="button" onClick={() => handleEnd(p)} className="text-xs text-amber-600 hover:underline">End</button>
                        )}
                        <button type="button" onClick={() => handleDelete(p.id)} className="text-xs text-rose-600 hover:underline">Delete</button>
                      </div>
                    )}
                    {canEdit && (() => {
                      const staffMember = staff.find((s) => s.id === p.staffId);
                      const { weekly, annual } = resolveProvisionCostDisplay(p, staffMember);
                      if (weekly <= 0 && annual <= 0) return null;
                      return (
                        <p className="text-xs text-gray-400 w-full">
                          {weekly > 0 && `Est. £${weekly.toFixed(2)}/week`}
                          {weekly > 0 && annual > 0 && ', '}
                          {annual > 0 && `£${annual.toFixed(0)}/year`}
                          {annual > 0 && ` (${SCHOOL_WEEKS_PER_YEAR} school weeks, SENCO view only)`}
                        </p>
                      );
                    })()}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))
      )}

      {showForm && canEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900">
            <h4 className="text-lg font-semibold mb-4">{editingId ? 'Edit provision' : 'Add provision'}</h4>

            <div className="space-y-3">
              <label className="block text-sm">
                Area of need
                <select
                  value={form.areaOfNeed}
                  onChange={(e) => setForm((f) => ({ ...f, areaOfNeed: e.target.value, presetKey: '', name: '' }))}
                  className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
                >
                  {Object.entries(AREA_OF_NEED_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>

              <label className="block text-sm">
                Common provision (or choose Custom)
                <select
                  value={form.presetKey || 'custom'}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      setForm((f) => ({ ...f, presetKey: '', name: f.name || '' }));
                    } else {
                      handlePresetChange(e.target.value);
                    }
                  }}
                  className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
                >
                  <option value="custom">Custom provision...</option>
                  {Object.entries(presetGroups).map(([cat, presets]) => (
                    <optgroup key={cat} label={PROVISION_CATEGORY_LABELS[cat]}>
                      {presets.map((p) => (
                        <option key={p.presetKey} value={p.presetKey}>{p.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {form.presetKey && getPresetByKey(form.presetKey)?.kbSlugs?.length > 0 && (
                  <KbResourceLinks slugs={getPresetByKey(form.presetKey).kbSlugs} className="mt-2" />
                )}
              </label>

              <label className="block text-sm">
                Name
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  Frequency
                  <input value={form.frequency} onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5 text-sm" />
                </label>
                <label className="block text-sm">
                  Duration
                  <input value={form.duration} onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5 text-sm" placeholder="e.g. 1 hour, Per lesson" />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  Ratio
                  <select value={form.ratio} onChange={(e) => setForm((f) => ({ ...f, ratio: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5 text-sm">
                    <option value="1:1">1:1</option>
                    <option value="1:2">1:2</option>
                    <option value="1:3">1:3</option>
                    <option value="small_group">Small group</option>
                    <option value="n/a">N/A</option>
                  </select>
                </label>
                <label className="block text-sm">
                  Staff member
                  <select value={form.staffId} onChange={(e) => setForm((f) => ({ ...f, staffId: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5 text-sm">
                    <option value="">Not assigned</option>
                    {staff.map((s) => (
                      <option key={s.id} value={s.id}>
                        {[s.firstName, s.lastName].filter(Boolean).join(' ') || s.name} ({s.role || 'Staff'})
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block text-sm">
                Staff role label (if not assigned)
                <input value={form.staffRoleLabel} onChange={(e) => setForm((f) => ({ ...f, staffRoleLabel: e.target.value }))} className="mt-1 w-full rounded border px-2 py-1.5 text-sm" placeholder="e.g. HLTA, Pastoral mentor" />
              </label>

              <label className="block text-sm">
                Notes
                <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="mt-1 w-full rounded border px-2 py-1.5 text-sm" />
              </label>

              {canEdit && (
                <p className="text-xs text-gray-500 rounded bg-gray-50 p-2">
                  Estimated cost (SENCO only): £{costPreview.weeklyCost.toFixed(2)}/week,
                  {' '}
                  £{costPreview.totalAnnual.toFixed(2)}/year ({SCHOOL_WEEKS_PER_YEAR} school weeks)
                </p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="rounded border px-3 py-2 text-sm">Cancel</button>
              <button type="button" onClick={handleSave} disabled={saving} className="rounded bg-indigo-600 px-3 py-2 text-sm text-white disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function getActiveProvisionsForDateRange(provisions, fromDate, toDate) {
  const from = fromDate ? new Date(fromDate).getTime() : 0;
  const to = toDate ? new Date(toDate).getTime() : Date.now();
  return (provisions || []).filter((p) => {
    if (p.status === 'ended' && p.endDate) {
      const end = new Date(p.endDate).getTime();
      if (end < from) return false;
    }
    if (p.startDate) {
      const start = new Date(p.startDate).getTime();
      if (start > to) return false;
    }
    return p.status === 'active' || p.status === 'planned';
  });
}
